
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  RadioGroup,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import FeatherIcon from "feather-icons-react";

import PageContainer from "../../../components/container/PageContainer";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomRadio from "../../../components/forms/custom-elements/CustomRadio";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";

import MissingTable from "../my-missing-punches/MissingTable";

import {
  AddMissingPunch,
  GetMissingPunches,
} from "../../../services/time-attendance/misspunches";

import {
  missingPunchesSchema,
} from "../../../validationSchema/TimeAttendance";

const BCrumb = [
  {
    to: "/",
    title: "Time and Attendance",
  },
  {
    title: "Missing Punch Dashboard",
  },
];

const getYesterday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - 1);

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const normalizeDate = (value) => {
  if (!value) return "";

  const dateValue = String(value).trim();

  const isoMatch = dateValue.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})/
  );

  if (isoMatch) {
    const [, year, month, day] = isoMatch;

    return [
      year,
      String(month).padStart(2, "0"),
      String(day).padStart(2, "0"),
    ].join("-");
  }

  const dayFirstMatch = dateValue.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/
  );

  if (dayFirstMatch) {
    const [, day, month, year] = dayFirstMatch;

    return [
      year,
      String(month).padStart(2, "0"),
      String(day).padStart(2, "0"),
    ].join("-");
  }

  return dateValue.split("T")[0].split(" ")[0];
};

const normalizeRecord = (record) => ({
  missingPunchId:
    record?.missingPunchId ??
    record?.MissingPunchId ??
    record?.id ??
    record?.Id ??
    0,

  employeeId:
    record?.employeeId ??
    record?.EmployeeId ??
    "",

  personName:
    record?.personName ??
    record?.PersonName ??
    "",

  departmentName:
    record?.departmentName ??
    record?.DepartmentName ??
    "",

  organization:
    record?.organization ??
    record?.Organization ??
    record?.organizationName ??
    record?.OrganizationName ??
    "",

  missingDate: normalizeDate(
    record?.missingDate ??
    record?.MissingDate ??
    record?.attendanceDate ??
    record?.AttendanceDate
  ),

  punchFor:
    record?.punchFor ??
    record?.PunchFor ??
    record?.missingPunchFor ??
    record?.MissingPunchFor ??
    "",

  punchType:
    record?.punchType ??
    record?.PunchType ??
    "normal",

  time:
    record?.time ??
    record?.Time ??
    "",

  inTime:
    record?.inTime ??
    record?.InTime ??
    "",

  outTime:
    record?.outTime ??
    record?.OutTime ??
    "",

  attendanceStatus:
    record?.attendanceStatus ??
    record?.AttendanceStatus ??
    record?.status ??
    record?.Status ??
    "",

  approvalStatus:
    record?.approvalStatus ??
    record?.ApprovalStatus ??
    "Pending",
});

const MissingPunchDashboard = () => {
  const [missingRecords, setMissingRecords] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);
  const [updatingMissingPunch, setUpdatingMissingPunch] =
    useState(false);

  const [selectedMissingRow, setSelectedMissingRow] =
    useState(null);

  const [missingErrorMessage, setMissingErrorMessage] =
    useState("");

  const [missingSuccessMessage, setMissingSuccessMessage] =
    useState("");

  const [selectedDepartmentForEmployees, setSelectedDepartmentForEmployees] =
    useState("");

  const loadMissingPunches = useCallback(async () => {
    try {
      setMissingLoading(true);
      setMissingErrorMessage("");

      const response = await GetMissingPunches();

      const result =
        response?.data?.data ??
        response?.data ??
        [];

      const records = Array.isArray(result)
        ? result
        : result?.records ??
          result?.items ??
          [];

      const normalizedRecords = records
        .map(normalizeRecord)
        .filter(
          (record) =>
            String(record.attendanceStatus ?? "")
              .trim()
              .toUpperCase() === "M"
        );

      setMissingRecords(normalizedRecords);
    } catch (error) {
      setMissingRecords([]);

      setMissingErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.title ||
          "Unable to load missing punch records."
      );
    } finally {
      setMissingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMissingPunches();
  }, [loadMissingPunches]);

  const formik = useFormik({
    initialValues: {
      missingPunchId: 0,
      punchfor: "In",
      punchtype: "normal",
      missingdate: "",
      time: "",
    },

    validationSchema: missingPunchesSchema,

    onSubmit: async (values, helpers) => {
      if (!values.missingPunchId) {
        setMissingErrorMessage(
          "Please select a missing punch record to edit."
        );
        return;
      }

      try {
        setUpdatingMissingPunch(true);
        setMissingErrorMessage("");

        await AddMissingPunch({
          missingPunchId: values.missingPunchId,
          punchFor: values.punchfor,
          punchType: values.punchtype,
          missingDate: values.missingdate,
          time: values.time,
        });

        helpers.resetForm({
          values: {
            missingPunchId: 0,
            punchfor: "In",
            punchtype: "normal",
            missingdate: "",
            time: "",
          },
        });

        setSelectedMissingRow(null);
        setMissingSuccessMessage(
          "Missing punch updated successfully."
        );

        await loadMissingPunches();
      } catch (error) {
        setMissingErrorMessage(
          error?.response?.data?.message ||
            error?.response?.data?.title ||
            "Unable to update missing punch."
        );
      } finally {
        setUpdatingMissingPunch(false);
      }
    },
  });

  const handleEdit = (row) => {
    if (!row?.missingPunchId) {
      setMissingErrorMessage(
        "Missing punch ID was not found."
      );
      return;
    }

    setMissingErrorMessage("");
    setSelectedMissingRow(row);

    formik.setValues({
      missingPunchId: row.missingPunchId,
      punchfor: row.punchFor || "In",
      punchtype: row.punchType || "normal",
      missingdate: normalizeDate(row.missingDate),
      time: row.time || "",
    });

    formik.setTouched({});
  };

  const handleCancelEdit = () => {
    setSelectedMissingRow(null);
    setMissingErrorMessage("");

    formik.resetForm({
      values: {
        missingPunchId: 0,
        punchfor: "In",
        punchtype: "normal",
        missingdate: "",
        time: "",
      },
    });
  };

  const stats = useMemo(() => {
    const total = missingRecords.length;

    const pending = missingRecords.filter(
      (record) =>
        String(record.approvalStatus || "Pending")
          .trim()
          .toLowerCase() === "pending"
    ).length;

    const approved = missingRecords.filter(
      (record) =>
        String(record.approvalStatus || "")
          .trim()
          .toLowerCase() === "approved"
    ).length;

    const departments = new Set(
      missingRecords
        .map((record) => record.departmentName)
        .filter(Boolean)
    ).size;

    return { total, pending, approved, departments };
  }, [missingRecords]);

  const statCards = [
    {
      title: "Missing Punches",
      value: stats.total,
      icon: "alert-circle",
      color: "error.main",
    },
    {
      title: "Pending",
      value: stats.pending,
      icon: "clock",
      color: "warning.main",
    }, 
    {
      title: "Departments",
      value: stats.departments,
      icon: "users",
      color: "primary.main",
    },
  ];
const departmentAnalysis = useMemo(() => {
  const departmentMap = new Map();

  missingRecords.forEach((record) => {
    const department =
      String(record.departmentName || "").trim() || "Unassigned";

    departmentMap.set(
      department,
      (departmentMap.get(department) || 0) + 1
    );
  });

  return Array.from(departmentMap.entries())
    .map(([department, count]) => ({
      department,
      count,
      percentage:
        missingRecords.length > 0
          ? (count / missingRecords.length) * 100
          : 0,
    }))
    .sort((a, b) => b.count - a.count);
}, [missingRecords]);

  const selectedDepartmentEmployees = useMemo(() => {
    if (!selectedDepartmentForEmployees) return [];

    const departmentRecords = missingRecords.filter((record) => {
      const department =
        String(record.departmentName || "").trim() || "Unassigned";

      return department === selectedDepartmentForEmployees;
    });

    const employeeMap = new Map();

    departmentRecords.forEach((record) => {
      const employeeId = String(record.employeeId ?? "").trim() || "-";
      const employeeName = String(record.personName ?? "").trim() || "Unknown";
      const employeeKey = `${employeeId.toLowerCase()}|${employeeName.toLowerCase()}`;

      if (!employeeMap.has(employeeKey)) {
        employeeMap.set(employeeKey, {
          employeeId,
          personName: employeeName,
          organization: String(record.organization ?? "").trim() || "-",
          missingPunchCount: 0,
        });
      }

      employeeMap.get(employeeKey).missingPunchCount += 1;
    });

    return Array.from(employeeMap.values()).sort((a, b) =>
      a.personName.localeCompare(b.personName)
    );
  }, [missingRecords, selectedDepartmentForEmployees]);

  return (
    <PageContainer
      title="Missing Punch Dashboard"
      description="View and manage missing attendance punches"
    >
      <Breadcrumb
        title="Missing Punch Dashboard"
        items={BCrumb}
      />

      <Box sx={{ width: "100%", minWidth: 0 }}>
        {missingErrorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setMissingErrorMessage("")}
          >
            {missingErrorMessage}
          </Alert>
        )}

        {/* Dashboard summary cards */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {statCards.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.title}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    p: 2.5,
                    "&:last-child": {
                      pb: 2.5,
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      variant="h4"
                      fontWeight={700}
                    >
                      {item.value}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      minWidth: 46,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: item.color,
                      bgcolor: "action.hover",
                    }}
                  >
                    <FeatherIcon
                      icon={item.icon}
                      size={23}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          

        </Grid>
{/* Department-wise Analysis */}
<Card sx={{ mb: 2.5, overflow: "hidden" }}>
  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1}
      mb={2}
    >
      <Box>
        <Typography variant="h6" fontWeight={700}>
          Department-wise Analysis
        </Typography>

        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Missing punch distribution across departments
        </Typography>
      </Box>

      <Chip
        color="primary"
        variant="outlined"
        label={`${departmentAnalysis.length} Departments`}
      />
    </Box>

    <Divider sx={{ mb: 2 }} />

    {departmentAnalysis.length === 0 ? (
      <Alert severity="info">
        No department-wise data available.
      </Alert>
    ) : (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                Department
              </TableCell>

              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Missing Punches
              </TableCell>

              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>
                Share of Total
              </TableCell>

              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Percentage
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {departmentAnalysis.map((item) => (
              <TableRow
                key={item.department}
                hover
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {item.department}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  <Chip
                    size="small"
                    color="warning"
                    label={item.count}
                    clickable
                    onClick={() =>
                      setSelectedDepartmentForEmployees(item.department)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedDepartmentForEmployees(item.department);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Show employees in ${item.department}`}
                    title={`View employees in ${item.department}`}
                    sx={{
                      cursor: "pointer",
                      fontWeight: 700,
                      "&:hover": { filter: "brightness(0.96)" },
                    }}
                  />
                </TableCell>

                <TableCell>
                  <LinearProgress
                    variant="determinate"
                    value={item.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 5,
                    }}
                  />
                </TableCell>

                <TableCell align="right">
                  {item.percentage.toFixed(1)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </CardContent>
</Card>

{/* Employee list for the selected department */}
<Dialog
  open={Boolean(selectedDepartmentForEmployees)}
  onClose={() => setSelectedDepartmentForEmployees("")}
  fullWidth
  maxWidth="md"
>
  <DialogTitle
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      pr: 1.5,
    }}
  >
    <Box>
      <Typography variant="h6" fontWeight={700}>
        Employees — {selectedDepartmentForEmployees}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Employees with missing punches in this department
      </Typography>
    </Box>

    <IconButton
      aria-label="Close employee list"
      onClick={() => setSelectedDepartmentForEmployees("")}
      size="small"
    >
      <FeatherIcon icon="x" size={18} />
    </IconButton>
  </DialogTitle>

  <DialogContent dividers sx={{ p: 0 }}>
    {selectedDepartmentEmployees.length === 0 ? (
      <Box p={3}>
        <Alert severity="info">
          No employees found for this department.
        </Alert>
      </Box>
    ) : (
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table stickyHeader size="small" aria-label="department employees">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 70 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Employee ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Employee Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Organization</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Missing Punches
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedDepartmentEmployees.map((employee, index) => (
              <TableRow hover key={`${employee.employeeId}-${employee.personName}`}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{employee.employeeId}</TableCell>
                <TableCell>{employee.personName}</TableCell>
                <TableCell>{employee.organization}</TableCell>
                <TableCell align="right">
                  <Chip
                    size="small"
                    color="warning"
                    label={employee.missingPunchCount}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </DialogContent>

  <DialogActions sx={{ px: 2, py: 1.5, justifyContent: "space-between" }}>
    <Typography variant="body2" color="text.secondary">
      {selectedDepartmentEmployees.length} employees
    </Typography>
    <Button
      onClick={() => setSelectedDepartmentForEmployees("")}
      variant="outlined"
      sx={{ textTransform: "none" }}
    >
      Close
    </Button>
  </DialogActions>
</Dialog>

        {/* Edit missing punch */}
        <Card sx={{ mb: 2.5, overflow: "hidden" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            p={2}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <FeatherIcon icon="edit-2" size={18} />

              <Typography fontWeight={600}>
                {selectedMissingRow
                  ? "Edit Missing Punch"
                  : "Missing Punch Details"}
              </Typography>
            </Box>

            {selectedMissingRow && (
              <Chip
                size="small"
                color="primary"
                label={`Employee: ${
                  selectedMissingRow.employeeId || "N/A"
                }`}
              />
            )}
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {!selectedMissingRow && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Select a record from the table below to edit
                its missing punch details.
              </Alert>
            )}

            <FormikProvider value={formik}>
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} lg={2.5}>
                    <CustomFormLabel
                      htmlFor="punchfor"
                      sx={{ mt: 0, mb: 1 }}
                    >
                      Missing Punch Is For
                    </CustomFormLabel>

                    <FormControl fullWidth>
                      <RadioGroup
                        row
                        name="punchfor"
                        value={formik.values.punchfor}
                        onChange={formik.handleChange}
                        sx={{ minHeight: 40, gap: 1 }}
                      >
                        <FormControlLabel
                          value="In"
                          control={<CustomRadio />}
                          label="In"
                          sx={{ m: 0 }}
                        />

                        <FormControlLabel
                          value="Out"
                          control={<CustomRadio />}
                          label="Out"
                          sx={{ m: 0 }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={2.5}>
                    <CustomFormLabel
                      htmlFor="punchtype"
                      sx={{ mt: 0, mb: 1 }}
                    >
                      Punch Type
                    </CustomFormLabel>

                    <FormControl fullWidth>
                      <RadioGroup
                        row
                        name="punchtype"
                        value={formik.values.punchtype}
                        onChange={formik.handleChange}
                        sx={{
                          minHeight: 40,
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <FormControlLabel
                          value="normal"
                          control={<CustomRadio />}
                          label="Normal"
                          sx={{ m: 0 }}
                        />

                        <FormControlLabel
                          value="lunch"
                          control={<CustomRadio />}
                          label="Lunch"
                          sx={{ m: 0 }}
                        />

                        <FormControlLabel
                          value="business"
                          control={<CustomRadio />}
                          label="Business"
                          sx={{ m: 0 }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} lg={2.5}>
                    <CustomFormLabel
                      htmlFor="missingdate"
                      sx={{ mt: 0, mb: 1 }}
                    >
                      Missing Date
                    </CustomFormLabel>

                    <CustomTextField
                      id="missingdate"
                      name="missingdate"
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: getYesterday() }}
                      value={formik.values.missingdate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={Boolean(
                        formik.touched.missingdate &&
                          formik.errors.missingdate
                      )}
                      helperText={
                        formik.touched.missingdate &&
                        formik.errors.missingdate
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} lg={2}>
                    <CustomFormLabel
                      htmlFor="missingTime"
                      sx={{ mt: 0, mb: 1 }}
                    >
                      Missing Time
                    </CustomFormLabel>

                    <CustomTextField
                      id="missingTime"
                      name="time"
                      type="time"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      value={formik.values.time}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={Boolean(
                        formik.touched.time &&
                          formik.errors.time
                      )}
                      helperText={
                        formik.touched.time &&
                        formik.errors.time
                      }
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    lg={2.5}
                    display="flex"
                    alignItems="flex-end"
                    gap={1}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={
                        !selectedMissingRow ||
                        updatingMissingPunch ||
                        !formik.values.missingdate ||
                        !formik.values.time
                      }
                      startIcon={
                        updatingMissingPunch ? (
                          <CircularProgress
                            size={16}
                            color="inherit"
                          />
                        ) : (
                          <FeatherIcon
                            icon="save"
                            size={16}
                          />
                        )
                      }
                      sx={{
                        minHeight: 40,
                        textTransform: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {updatingMissingPunch
                        ? "Updating..."
                        : "Update Punch"}
                    </Button>

                    {selectedMissingRow && (
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={handleCancelEdit}
                        disabled={updatingMissingPunch}
                        sx={{
                          minHeight: 40,
                          textTransform: "none",
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Form>
            </FormikProvider>
          </CardContent>
        </Card>

        {/* Missing punch records */}
        <Card sx={{ overflow: "hidden" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            p={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Missing Punch Records
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Attendance records with missing punch status
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={loadMissingPunches}
              disabled={missingLoading}
              startIcon={
                missingLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <FeatherIcon icon="refresh-cw" size={16} />
                )
              }
              sx={{ textTransform: "none" }}
            >
              Refresh
            </Button>
          </Box>

          <CardContent sx={{ p: 0 }}>
            {missingLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                gap={1.5}
                py={6}
              >
                <CircularProgress size={32} />

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Loading missing punch records...
                </Typography>
              </Box>
            ) : missingRecords.length === 0 ? (
              <Box px={2} pb={3}>
                <Alert severity="success">
                  No missing punch records found.
                </Alert>
              </Box>
            ) : (
              <MissingTable
                missingPunchRecords={missingRecords}
                onEdit={handleEdit}
                selectedMissingPunchId={
                  selectedMissingRow?.missingPunchId || 0
                }
              />
            )}
          </CardContent>
        </Card>

        <Snackbar
          open={Boolean(missingSuccessMessage)}
          autoHideDuration={3000}
          onClose={() => setMissingSuccessMessage("")}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setMissingSuccessMessage("")}
          >
            {missingSuccessMessage}
          </Alert>
        </Snackbar>
      </Box>
    </PageContainer>
  );
};

export default MissingPunchDashboard;
